/**
 * Dashboard strings in English and Czech. The site's labels come from its hotpot-cms.yaml; these are the CMS's
 * own words. The language follows the site's `ui_language`, the browser, or the user's choice in the menu.
 */
import { createContext, useContext } from 'react';

export type Lang = 'en' | 'cs';

const en = {
  livePreview: 'Live preview',
  showLivePreview: 'Show live preview',
  hideLivePreview: 'Hide live preview',
  lpDesktop: 'Computer',
  lpPhone: 'Phone',
  lpReload: 'Reload',
  lpOpen: 'Open',
  lpLive: 'Changes show here as you type.',
  lpNeedsSave: 'Some changes (order, new items, dates) show after you save a draft.',
  lpBuilding: 'The page is being rebuilt with your saved changes…',
  lpNoBridge: 'This site can’t show changes as you type yet; they show after saving.',
  showPanel: 'Show the menu',
  hidePanel: 'Hide the menu',
  changeCreated: 'This change created it.',
  changeDeleted: 'This change deleted it.',
  restoreBefore: 'Restore the version before this change',
  restoreDeleted: 'Bring it back',
  alreadyBefore: 'It is already as it was before this change.',
  othersEditing: '{names} has this open right now. If you both save, the CMS combines your changes; if you change the same field, it asks which to keep.',
  mergedOthers: 'Someone saved this meanwhile: their changes and yours are combined.',
  conflictFields: 'Someone else saved this page meanwhile and also changed: {fields}. Keep your version of those fields, or load theirs (your changes on this page are lost)?',
  keepMine: 'Keep mine',
  takeTheirs: 'Load theirs',
  uploadFile: 'Upload a file',
  uploadingPct: 'Uploading… {n} %',
  openFile: 'Open ↗',
  fileUrlPlaceholder: 'https://… (or upload a file)',
  fileUploadFailed: 'The upload failed: {error}',
  unknownBlock: 'This block has a type the CMS doesn’t know ({type}); it is kept as it is.',
  saveDraft: 'Save draft',
  savePublish: 'Save and publish',
  publishAll: 'Publish',
  preview: 'Preview',
  savedDraft: 'Saved as a draft. Preparing the preview…',
  previewPending: 'Preparing preview…',
  previewLive: 'Preview ready',
  previewFailed: 'Preview failed',
  previewDemo: 'The demo has no preview build. In the real CMS this opens the preview version of the site, with your drafts in it.',
  pendingCount: 'Unpublished changes: {n}',
  pendingNone: 'Everything is published',
  pendingShow: 'What changed',
  pendingIntro: 'These changes are saved and visible in the preview, but not on the live site yet:',
  discardAll: 'Discard',
  discardTitle: 'Discard unpublished changes?',
  discardText: 'All {n} unpublished changes will be deleted. The live site stays as it is.',
  discarded: 'Unpublished changes discarded',
  publishTitle: 'Publish to the live site?',
  publishText: 'These changes will go live on {site}:',
  published: 'Published. The site is rebuilding…',
  nothingToPublish: 'Nothing to publish',
  draftConflict: 'The live site changed the same content in the meantime. Discard your unpublished changes and make them again, or ask Hotpot Studio.',
  draftsHint: 'Saves are drafts you can check in the preview; Publish puts them on the site.',
  previewBranch: 'Preview branch',
  previewBranchHint: 'Saves go here first (drafts); Cloudflare Pages builds it as a preview. Empty = every save goes live right away.',
  previewUrl: 'Preview address',
  previewUrlHint: 'Cloudflare Pages: https://<branch>.<project>.pages.dev',
  collections: 'Collections',
  globalSettings: 'Global settings',
  search: 'Search...',
  searchNoResults: 'Nothing found',
  new: 'New+',
  delete: 'Delete',
  save: 'Save',
  saving: 'Saving…',
  saved: 'Saved',
  cancel: 'Cancel',
  close: 'Close',
  back: 'Back',
  duplicate: 'Duplicate',
  history: 'History',
  viewOnSite: 'View on site ↗',
  openSite: 'Open site ↗',
  remove: 'Remove',
  upload: 'Upload',
  replace: 'Replace',
  chooseFromLibrary: 'From library',
  add: 'Add',
  edit: 'Edit',
  moreOptions: 'More options',
  required: 'required',
  optional: 'optional',
  untitled: 'Untitled',
  loading: 'Loading…',
  retry: 'Try again',
  allSites: 'All sites',
  sites: 'Sites',
  media: 'Media',
  users: 'People',
  account: 'Account',
  logout: 'Log out',
  privacy: 'Privacy',
  language: 'Language',
  home: 'Start',

  // login
  loginTitle: 'Log in',
  loginIntro: 'Enter your email. We’ll send you a link and a code to log in. No password needed.',
  email: 'Email',
  sendLink: 'Send login link',
  sending: 'Sending…',
  checkInbox: 'Check your inbox',
  checkInboxText: 'If {email} has access, a login link is on its way. Open it on this device, or type the 6-digit code from the email here.',
  code: 'Code from the email',
  logIn: 'Log in',
  useOtherEmail: 'Use another email',
  resend: 'Send a new link',
  verifying: 'Logging you in…',
  linkExpired: 'This link has expired or was already used',
  linkExpiredText: 'Login links work once and for 15 minutes (invitations for 7 days). Ask for a new one below.',
  invalidCode: 'That code isn’t right. Check the newest email and try again.',
  tooManyAttempts: 'Too many tries. Ask for a new link.',
  tooManyRequests: 'Too many login emails. Try again in an hour.',
  invalidEmail: 'Enter a valid email address.',
  loggedOut: 'You’re logged out.',
  loginPrivacy:
    'We use your email only to log you in and to show who made each change. {operator} runs this service. Details in the privacy notice.',

  // sites
  yourSites: 'Your sites',
  githubConnect: 'Connect GitHub',
  githubConnectText: 'One last step: connect GitHub so the CMS can save to the sites’ repositories. GitHub opens with everything filled in; click “Create GitHub App”, then pick the repositories and “Install”.',
  githubConnected: 'GitHub is connected.',
  githubRepos: 'Choose which repositories the CMS can edit',
  addSite: 'Add site',
  lastEdit: 'Last edit',
  noSites: 'You don’t have access to any site yet. Ask the person who invited you, or Hotpot Studio.',
  siteId: 'Short name (used in links)',
  siteName: 'Site name',
  repo: 'GitHub repository (owner/name)',
  branch: 'Branch',
  branchHint: 'Saves are committed to this branch. Use a test branch (its Cloudflare preview) before switching to main.',
  liveUrl: 'Live site address',
  addSiteSteps1: 'Build the Astro site with its content in src/content (as usual).',
  addSiteSteps2: 'Add hotpot-cms.yaml to the repository root (convert the Sveltia config with pnpm convert).',
  addSiteSteps3: 'Install the Hotpot CMS GitHub App on the repository.',
  addSiteSteps4: 'Fill in the form below, then invite the client from People.',

  // start screen
  quickActions: 'Quick actions',
  recentEdits: 'Recent edits',
  noRecent: 'No edits yet.',
  by: 'by',
  editPage: 'Edit',

  // list
  items: '{n} items',
  selected: '{n} selected',
  selectAll: 'Select all',
  sortBy: 'Sort',
  sortManual: 'Manual order',
  newestFirst: 'newest first',
  oldestFirst: 'oldest first',
  az: 'A–Z',
  za: 'Z–A',
  filterAll: 'All',
  filterPublished: 'On the site',
  filterHidden: 'Hidden',
  hidden: 'Hidden',
  searchIn: 'Search in {name}…',
  emptyCollection: 'Nothing here yet.',
  emptySearch: 'Nothing matches “{q}”.',
  dragToReorder: 'Drag to change the order on the site.',
  saveOrder: 'Save order',
  orderSaved: 'Order saved',
  deleteConfirmTitle: 'Delete {n}?',
  deleteConfirmText: 'This removes {names} from the site. You can bring it back from History.',
  deleteForever: 'Delete',
  deleted: 'Deleted',

  // editor
  newEntry: 'New {name}',
  visibleOnSite: 'Visible on the site',
  hiddenOnSite: 'Hidden on the site',
  unsaved: 'Unsaved changes',
  unsavedTitle: 'Leave without saving?',
  unsavedText: 'Your changes on this page will be lost.',
  leave: 'Leave',
  stay: 'Stay',
  fixErrors: 'Please fix the highlighted fields.',
  conflictTitle: 'Someone else changed this',
  conflictText: 'This page was saved by someone else after you opened it. Reload to see their version (your changes on this page will be lost), or copy your text first.',
  reload: 'Reload',
  savedPublishing: 'Saved. Publishing…',
  translationFallback: 'Empty = the {lang} text is shown.',
  trWorking: 'Translating…',
  trAuto: 'Translated automatically: please check it.',
  trOff: 'Automatic translation is not switched on.',
  trFailed: 'The translation did not work. Write it by hand or try again.',
  trAgain: 'Translate again',
  sameInAllLanguages: 'Fields without a translation are the same in every language.',
  onlyTranslatable: 'Show only translatable fields',
  duplicateSuffix: '(copy)',
  duplicateNote: 'This is a copy. It is hidden on the site until you switch it on.',
  deleteEntryTitle: 'Delete “{name}”?',
  deleteEntryText: 'It disappears from the site after the next publish. You can bring it back from History.',
  addItem: 'Add {name}',
  item: 'item',
  moveUp: 'Move up',
  moveDown: 'Move down',
  noImage: 'No image',
  dropImage: 'Drop an image here or',
  dropImages: 'Drop photos here or click',
  dropMedia: 'Drop photos or videos here or click',
  galleryItemTitle: 'Photo or video',
  videoLink: 'Video link',
  processing: 'Preparing image…',
  imageTooBig: '{name} is too big (max {mb} MB).',
  notAnImage: '{name} is not an image.',
  youtubeOk: 'Video {id}',
  youtubeOpen: 'Open on YouTube ↗',
  linkPrompt: 'Link address (https://…)',
  imageAlt: 'Image description (for screen readers)',
  richText: 'Text',
  markdownSource: 'Markdown',
  none: '—',
  choose: 'Choose…',
  searchOptions: 'Search…',

  // validation
  v_required: 'This field is required.',
  v_pattern: 'This doesn’t look right.',
  v_min: 'At least {limit}.',
  v_max: 'At most {limit}.',
  v_minItems: 'Add at least {limit}.',
  v_maxItems: 'At most {limit} allowed.',
  v_number: 'Enter a number.',
  v_integer: 'Enter a whole number.',
  v_url: 'Enter a full address starting with https://',
  v_youtube: 'Paste a YouTube video link.',
  v_date: 'Enter a date.',
  v_option: 'Choose one of the options.',

  // publish
  publishPending: 'Publishing…',
  publishBuilding: 'Publishing…',
  publishLive: 'Live',
  publishFailed: 'Publishing failed',
  publishFailedText: 'The site didn’t rebuild. Your change is saved; Hotpot Studio has the build log.',
  publishUnknown: 'Saved',
  buildLog: 'Build log ↗',

  // media
  mediaTitle: 'Media',
  mediaIntro: 'Every image on the site. Upload new ones here or straight from a field.',
  allFolders: 'All folders',
  usedIn: 'Used in',
  usedInN: 'Used in {n}',
  unused: 'Not used',
  uploadTo: 'Upload to',
  replaceImage: 'Replace image',
  replaceNote: 'Replacing keeps the file name, so every page that uses it shows the new image.',
  deleteImage: 'Delete image',
  deleteImageUsed: 'This image is used, so it can’t be deleted. Remove it from those pages first.',
  pick: 'Use this image',
  uploaded: 'Uploaded',

  // history
  historyTitle: 'History',
  historyIntro: 'Every save is kept. Open a change to see what it did, or bring an older version back.',
  historyEntry: 'Versions of this page',
  restore: 'Restore this version',
  restoreConfirm: 'Restore the version from {date}? It is saved as a new change; nothing is lost.',
  restored: 'Restored',
  current: 'Current',
  changes: 'What changed',
  noChanges: 'No visible changes.',
  initialVersion: 'First version',

  // users
  usersTitle: 'People',
  usersIntro: 'Who can edit {site}. Owners can invite others; editors can only edit content.',
  invite: 'Invite',
  inviteTitle: 'Invite someone',
  name: 'Name',
  role: 'Role',
  roleOwner: 'Owner',
  roleEditor: 'Editor',
  roleStudio: 'Hotpot Studio',
  invited: 'Invited',
  lastLogin: 'Last login',
  never: 'never',
  removeAccess: 'Remove access',
  removeAccessConfirm: 'Remove {email} from {site}? They won’t be able to edit it any more.',
  inviteSent: 'Invitation sent to {email}',
  activity: 'Activity',

  // account / privacy
  yourName: 'Your name',
  yourNameHint: 'Shown as the author of your changes.',
  privacyTitle: 'Privacy',
  notFound: 'Page not found',
  error: 'Something went wrong',
  justNow: 'just now',
  minutesAgo: '{n} min ago',
  hoursAgo: '{n} h ago',
  daysAgo: '{n} days ago',
  dayAgo: '1 day ago',
};

export type StringKey = keyof typeof en;

const cs: Record<StringKey, string> = {
  livePreview: 'Živý náhled',
  showLivePreview: 'Zobrazit živý náhled',
  hideLivePreview: 'Skrýt živý náhled',
  lpDesktop: 'Počítač',
  lpPhone: 'Telefon',
  lpReload: 'Načíst znovu',
  lpOpen: 'Otevřít',
  lpLive: 'Změny se tu ukazují, jak píšete.',
  lpNeedsSave: 'Některé změny (pořadí, nové položky, data) uvidíte po uložení konceptu.',
  lpBuilding: 'Stránka se přestavuje s uloženými změnami…',
  lpNoBridge: 'Tento web zatím neumí ukazovat změny při psaní; uvidíte je po uložení.',
  showPanel: 'Zobrazit menu',
  hidePanel: 'Skrýt menu',
  changeCreated: 'Tato změna záznam vytvořila.',
  changeDeleted: 'Tato změna záznam smazala.',
  restoreBefore: 'Vrátit verzi před touto změnou',
  restoreDeleted: 'Obnovit',
  alreadyBefore: 'Už je to tak, jak to bylo před touto změnou.',
  othersEditing: '{names} to má právě otevřené. Když uložíte oba, CMS vaše změny spojí; pokud změníte stejné pole, zeptá se, kterou verzi nechat.',
  mergedOthers: 'Mezitím to uložil někdo jiný: jeho a vaše změny jsou spojené.',
  conflictFields: 'Mezitím to uložil někdo jiný a změnil i: {fields}. Ponechat u těchto polí vaši verzi, nebo načíst jeho (vaše změny na této stránce se ztratí)?',
  keepMine: 'Ponechat moje',
  takeTheirs: 'Načíst jeho verzi',
  uploadFile: 'Nahrát soubor',
  uploadingPct: 'Nahrávám… {n} %',
  openFile: 'Otevřít ↗',
  fileUrlPlaceholder: 'https://… (nebo nahrajte soubor)',
  fileUploadFailed: 'Nahrání se nepovedlo: {error}',
  unknownBlock: 'Tento blok má typ, který CMS nezná ({type}); zůstane, jak je.',
  saveDraft: 'Uložit koncept',
  savePublish: 'Uložit a zveřejnit',
  publishAll: 'Zveřejnit',
  preview: 'Náhled',
  savedDraft: 'Uloženo jako koncept. Připravuji náhled…',
  previewPending: 'Připravuji náhled…',
  previewLive: 'Náhled připraven',
  previewFailed: 'Náhled se nepovedl',
  previewDemo: 'Ukázka náhled nestaví. Ve skutečném CMS se otevře náhledová verze webu i s vašimi koncepty.',
  pendingCount: 'Neveřejněné změny: {n}',
  pendingNone: 'Vše je zveřejněné',
  pendingShow: 'Co se změnilo',
  pendingIntro: 'Tyto změny jsou uložené a vidíte je v náhledu, na webu zatím nejsou:',
  discardAll: 'Zahodit',
  discardTitle: 'Zahodit neveřejněné změny?',
  discardText: 'Všechny neveřejněné změny ({n}) se smažou. Web zůstane, jak je.',
  discarded: 'Neveřejněné změny zahozeny',
  publishTitle: 'Zveřejnit na webu?',
  publishText: 'Na web {site} půjdou tyto změny:',
  published: 'Zveřejněno. Web se přestavuje…',
  nothingToPublish: 'Není co zveřejnit',
  draftConflict: 'Mezitím se na webu změnil stejný obsah. Zahoďte neveřejněné změny a udělejte je znovu, nebo napište Hotpot Studiu.',
  draftsHint: 'Uložení je koncept, který uvidíte v náhledu; Zveřejnit ho pošle na web.',
  previewBranch: 'Náhledová větev',
  previewBranchHint: 'Sem se ukládají koncepty; Cloudflare Pages z ní staví náhled. Prázdné = každé uložení jde hned na web.',
  previewUrl: 'Adresa náhledu',
  previewUrlHint: 'Cloudflare Pages: https://<větev>.<projekt>.pages.dev',
  collections: 'Obsah',
  globalSettings: 'Nastavení webu',
  search: 'Hledat...',
  searchNoResults: 'Nic nenalezeno',
  new: 'Nový+',
  delete: 'Smazat',
  save: 'Uložit',
  saving: 'Ukládám…',
  saved: 'Uloženo',
  cancel: 'Zrušit',
  close: 'Zavřít',
  back: 'Zpět',
  duplicate: 'Duplikovat',
  history: 'Historie',
  viewOnSite: 'Zobrazit na webu ↗',
  openSite: 'Otevřít web ↗',
  remove: 'Odebrat',
  upload: 'Nahrát',
  replace: 'Vyměnit',
  chooseFromLibrary: 'Z knihovny',
  add: 'Přidat',
  edit: 'Upravit',
  moreOptions: 'Další možnosti',
  required: 'povinné',
  optional: 'nepovinné',
  untitled: 'Bez názvu',
  loading: 'Načítám…',
  retry: 'Zkusit znovu',
  allSites: 'Všechny weby',
  sites: 'Weby',
  media: 'Obrázky',
  users: 'Lidé',
  account: 'Účet',
  logout: 'Odhlásit se',
  privacy: 'Ochrana údajů',
  language: 'Jazyk',
  home: 'Úvod',

  loginTitle: 'Přihlášení',
  loginIntro: 'Zadejte svůj e-mail. Pošleme vám odkaz a kód pro přihlášení. Žádné heslo.',
  email: 'E-mail',
  sendLink: 'Poslat odkaz',
  sending: 'Posílám…',
  checkInbox: 'Zkontrolujte e-mail',
  checkInboxText: 'Pokud má {email} přístup, odkaz pro přihlášení je na cestě. Otevřete ho v tomto zařízení, nebo sem opište šestimístný kód z e-mailu.',
  code: 'Kód z e-mailu',
  logIn: 'Přihlásit se',
  useOtherEmail: 'Použít jiný e-mail',
  resend: 'Poslat nový odkaz',
  verifying: 'Přihlašuji…',
  linkExpired: 'Odkaz už neplatí',
  linkExpiredText: 'Odkaz pro přihlášení platí jednou a 15 minut (pozvánka 7 dní). Nechte si poslat nový.',
  invalidCode: 'Kód nesedí. Zkontrolujte nejnovější e-mail a zkuste to znovu.',
  tooManyAttempts: 'Příliš mnoho pokusů. Nechte si poslat nový odkaz.',
  tooManyRequests: 'Příliš mnoho e-mailů. Zkuste to za hodinu.',
  invalidEmail: 'Zadejte platnou e-mailovou adresu.',
  loggedOut: 'Jste odhlášeni.',
  loginPrivacy: 'E-mail používáme jen k přihlášení a k označení, kdo co upravil. Službu provozuje {operator}. Podrobnosti v informacích o ochraně údajů.',

  yourSites: 'Vaše weby',
  githubConnect: 'Propojit GitHub',
  githubConnectText: 'Poslední krok: propojte GitHub, aby CMS mohlo ukládat do repozitářů webů. Otevře se GitHub s vyplněným formulářem; klikněte na „Create GitHub App“, pak vyberte repozitáře a „Install“.',
  githubConnected: 'GitHub je propojený.',
  githubRepos: 'Vybrat repozitáře, které CMS smí upravovat',
  addSite: 'Přidat web',
  lastEdit: 'Poslední úprava',
  noSites: 'Zatím nemáte přístup k žádnému webu. Ozvěte se tomu, kdo vás pozval, nebo Hotpot Studiu.',
  siteId: 'Krátký název (do odkazů)',
  siteName: 'Název webu',
  repo: 'Repozitář na GitHubu (vlastník/název)',
  branch: 'Větev',
  branchHint: 'Uložení se zapisují do této větve. Než přepnete na main, vyzkoušejte to na testovací větvi (její náhled na Cloudflare).',
  liveUrl: 'Adresa webu',
  addSiteSteps1: 'Postavte web v Astru s obsahem v src/content (jako obvykle).',
  addSiteSteps2: 'Přidejte do kořene repozitáře hotpot-cms.yaml (z configu Sveltie příkazem pnpm convert).',
  addSiteSteps3: 'Nainstalujte na repozitář GitHub App „Hotpot CMS“.',
  addSiteSteps4: 'Vyplňte formulář a v sekci Lidé pozvěte klienta.',

  quickActions: 'Rychlé akce',
  recentEdits: 'Poslední úpravy',
  noRecent: 'Zatím žádné úpravy.',
  by: '·',
  editPage: 'Upravit',

  items: '{n} položek',
  selected: 'Vybráno: {n}',
  selectAll: 'Vybrat vše',
  sortBy: 'Řadit',
  sortManual: 'Vlastní pořadí',
  newestFirst: 'od nejnovějších',
  oldestFirst: 'od nejstarších',
  az: 'A–Z',
  za: 'Z–A',
  filterAll: 'Vše',
  filterPublished: 'Na webu',
  filterHidden: 'Skryté',
  hidden: 'Skryto',
  searchIn: 'Hledat v sekci {name}…',
  emptyCollection: 'Zatím tu nic není.',
  emptySearch: 'Na „{q}“ nic neodpovídá.',
  dragToReorder: 'Přetažením změníte pořadí na webu.',
  saveOrder: 'Uložit pořadí',
  orderSaved: 'Pořadí uloženo',
  deleteConfirmTitle: 'Smazat {n}?',
  deleteConfirmText: 'Z webu zmizí: {names}. Vrátit to jde v Historii.',
  deleteForever: 'Smazat',
  deleted: 'Smazáno',

  newEntry: '{name} – nový',
  visibleOnSite: 'Zobrazeno na webu',
  hiddenOnSite: 'Skryto na webu',
  unsaved: 'Neuložené změny',
  unsavedTitle: 'Odejít bez uložení?',
  unsavedText: 'Změny na této stránce se ztratí.',
  leave: 'Odejít',
  stay: 'Zůstat',
  fixErrors: 'Opravte prosím zvýrazněná pole.',
  conflictTitle: 'Někdo jiný to mezitím upravil',
  conflictText: 'Tuto stránku po vás uložil někdo jiný. Načtěte ji znovu, abyste viděli jeho verzi (vaše změny se ztratí), nebo si nejdřív zkopírujte svůj text.',
  reload: 'Načíst znovu',
  savedPublishing: 'Uloženo. Zveřejňuji…',
  translationFallback: 'Prázdné = zobrazí se text v jazyce {lang}.',
  trWorking: 'Překládám…',
  trAuto: 'Přeloženo automaticky, zkontrolujte to prosím.',
  trOff: 'Automatický překlad není zapnutý.',
  trFailed: 'Překlad se nepovedl. Napište ho ručně nebo to zkuste znovu.',
  trAgain: 'Přeložit znovu',
  sameInAllLanguages: 'Pole bez překladu jsou ve všech jazycích stejná.',
  onlyTranslatable: 'Zobrazit jen překládaná pole',
  duplicateSuffix: '(kopie)',
  duplicateNote: 'Tohle je kopie. Na webu je skrytá, dokud ji nezapnete.',
  deleteEntryTitle: 'Smazat „{name}“?',
  deleteEntryText: 'Z webu zmizí po dalším zveřejnění. Vrátit to jde v Historii.',
  addItem: 'Přidat: {name}',
  item: 'položku',
  moveUp: 'Posunout nahoru',
  moveDown: 'Posunout dolů',
  noImage: 'Bez obrázku',
  dropImage: 'Přetáhněte sem obrázek nebo',
  dropImages: 'Přetáhněte sem fotky nebo klikněte',
  dropMedia: 'Přetáhněte sem fotky nebo videa, nebo klikněte',
  galleryItemTitle: 'Fotka nebo video',
  videoLink: 'Odkaz na video',
  processing: 'Připravuji obrázek…',
  imageTooBig: '{name} je moc velký (max. {mb} MB).',
  notAnImage: '{name} není obrázek.',
  youtubeOk: 'Video {id}',
  youtubeOpen: 'Otevřít na YouTube ↗',
  linkPrompt: 'Adresa odkazu (https://…)',
  imageAlt: 'Popis obrázku (pro čtečky obrazovky)',
  richText: 'Text',
  markdownSource: 'Markdown',
  none: '—',
  choose: 'Vyberte…',
  searchOptions: 'Hledat…',

  v_required: 'Tohle pole je povinné.',
  v_pattern: 'Tohle nevypadá správně.',
  v_min: 'Nejméně {limit}.',
  v_max: 'Nejvýše {limit}.',
  v_minItems: 'Přidejte aspoň {limit}.',
  v_maxItems: 'Nejvýše {limit}.',
  v_number: 'Zadejte číslo.',
  v_integer: 'Zadejte celé číslo.',
  v_url: 'Zadejte celou adresu začínající https://',
  v_youtube: 'Vložte odkaz na video na YouTube.',
  v_date: 'Zadejte datum.',
  v_option: 'Vyberte jednu z možností.',

  publishPending: 'Zveřejňuji…',
  publishBuilding: 'Zveřejňuji…',
  publishLive: 'Na webu',
  publishFailed: 'Zveřejnění selhalo',
  publishFailedText: 'Web se nepřestavěl. Změna je uložená; Hotpot Studio má záznam o chybě.',
  publishUnknown: 'Uloženo',
  buildLog: 'Záznam buildu ↗',

  mediaTitle: 'Obrázky',
  mediaIntro: 'Všechny obrázky na webu. Nahrávat jde tady nebo rovnou u pole.',
  allFolders: 'Všechny složky',
  usedIn: 'Použito v',
  usedInN: 'Použito: {n}×',
  unused: 'Nepoužito',
  uploadTo: 'Nahrát do',
  replaceImage: 'Vyměnit obrázek',
  replaceNote: 'Výměna zachová název souboru, takže se nový obrázek ukáže všude, kde je použitý.',
  deleteImage: 'Smazat obrázek',
  deleteImageUsed: 'Obrázek je použitý, proto ho nejde smazat. Nejdřív ho odeberte z těchto stránek.',
  pick: 'Použít tento obrázek',
  uploaded: 'Nahráno',

  historyTitle: 'Historie',
  historyIntro: 'Každé uložení zůstává. Otevřete změnu a uvidíte, co udělala, nebo vraťte starší verzi.',
  historyEntry: 'Verze této stránky',
  restore: 'Vrátit tuto verzi',
  restoreConfirm: 'Vrátit verzi z {date}? Uloží se jako nová změna, nic se neztratí.',
  restored: 'Vráceno',
  current: 'Aktuální',
  changes: 'Co se změnilo',
  noChanges: 'Žádné viditelné změny.',
  initialVersion: 'První verze',

  usersTitle: 'Lidé',
  usersIntro: 'Kdo může upravovat {site}. Správci můžou zvát další lidi, editoři jen upravují obsah.',
  invite: 'Pozvat',
  inviteTitle: 'Pozvat člověka',
  name: 'Jméno',
  role: 'Role',
  roleOwner: 'Správce',
  roleEditor: 'Editor',
  roleStudio: 'Hotpot Studio',
  invited: 'Pozván(a)',
  lastLogin: 'Poslední přihlášení',
  never: 'nikdy',
  removeAccess: 'Odebrat přístup',
  removeAccessConfirm: 'Odebrat {email} z webu {site}? Už ho nebude moct upravovat.',
  inviteSent: 'Pozvánka odeslána na {email}',
  activity: 'Aktivita',

  yourName: 'Vaše jméno',
  yourNameHint: 'Zobrazí se jako autor vašich změn.',
  privacyTitle: 'Ochrana osobních údajů',
  notFound: 'Stránka nenalezena',
  error: 'Něco se pokazilo',
  justNow: 'právě teď',
  minutesAgo: 'před {n} min',
  hoursAgo: 'před {n} h',
  daysAgo: 'před {n} d',
  dayAgo: 'včera',
};

const dictionaries: Record<Lang, Record<StringKey, string>> = { en, cs };

export type T = (key: StringKey, vars?: Record<string, string | number>) => string;

export function translator(lang: Lang): T {
  const dict = dictionaries[lang] ?? en;
  return (key, vars) => {
    let s = dict[key] ?? en[key] ?? key;
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
    return s;
  };
}

export function browserLang(): Lang {
  try {
    const stored = localStorage.getItem('hp-lang');
    if (stored === 'cs' || stored === 'en') return stored;
  } catch {
    /* storage blocked */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'en';
  return /^(cs|sk)/i.test(nav) ? 'cs' : 'en';
}

export const LangContext = createContext<{ lang: Lang; t: T; setLang: (l: Lang) => void }>({
  lang: 'en',
  t: translator('en'),
  setLang: () => {},
});

export const useT = () => useContext(LangContext);

export const LOCALE_NAMES: Record<string, { cs: string; en: string }> = {
  cs: { cs: 'čeština', en: 'Czech' },
  en: { cs: 'angličtina', en: 'English' },
  it: { cs: 'italština', en: 'Italian' },
  de: { cs: 'němčina', en: 'German' },
  sk: { cs: 'slovenština', en: 'Slovak' },
};
